import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { allowDrop, getDropPayload } from "@/dnd";
import { db } from "@/lib/firebase";
import {
  collection as fsCollection,
  onSnapshot,
  addDoc,
  serverTimestamp,
  query,
  orderBy,
  doc,
  deleteDoc,
  getDocs,
  writeBatch,
} from "firebase/firestore";
import { PLAY_FORMATS } from "@/constants/formats";
import { FoilStyles, IllegalStyles } from "@/components/cards/CardTile";
import { isIllegalInFormat } from "@/utils/mtg";

// Minimal card shape (matches MyCollection's UserCardDoc)
export type UserCardLite = {
  id: string;  scryfall_id?: string | null;
  name: string;
  image?: string | null;
  custom_image_url?: string | null;
  type_line?: string | null;
  colors?: string[] | null;
  color_identity?: string[] | null;
  mana_value?: number | null;
  scryfall_full?: any | null;

  // listing fields (NOT from scryfall) — used in overlays/sort where available
  rarity?: string | null;
  set_code?: string | null;
  set_name?: string | null;
  collector_number?: string | null;
  price?: number | null;
  foil_price?: number | null;
};

// Deck types
type DeckDoc = {
  id: string;
  name: string;
  format?: string;
  commander_card_id?: string | null;
  cover_image?: string | null;
  color_identity?: string[] | null;
  position?: number;
  public?: boolean; // <--- added
  createdAt?: any;
  updatedAt?: any;
};

type DeckSlot = {
  id: string;
  userCardId: string;
  role?: string | null;
  createdAt?: any;
};

function ColorPips({ ci }: { ci?: string[] | null }) {
  if (!Array.isArray(ci) || ci.length === 0) return null;
  const map: Record<string, string> = { W: "#f8f4cf", U: "#67a6ff", B: "#6b6b6b", R: "#ff6b6b", G: "#6bff7a" };
  const order: Array<"W"|"U"|"B"|"R"|"G"> = ["W","U","B","R","G"];
  const uniq = Array.from(new Set(ci.map((x) => String(x).toUpperCase()))).filter((x) => order.includes(x as any)) as Array<"W"|"U"|"B"|"R"|"G">;
  const sorted = order.filter((o) => uniq.includes(o));
  return (
    <span className="inline-flex items-center gap-1">
      {sorted.map((c) => (
        <span key={c} className="inline-block w-3 h-3 rounded-full border border-black/40" style={{ backgroundColor: map[c] }} />
      ))}
    </span>
  );
}

function useMountedRef() {
  const m = useRef(true);
  useEffect(() => () => { m.current = false; }, []);
  return m;
}

// Helper: map card id -> card
function useCardsIndex<T extends { id: string }>(cards: T[] | null | undefined) {
  return useMemo(() => {
    const m = new Map<string, T>();
    (cards ?? []).forEach((c) => m.set(c.id, c));
    return m;
  }, [cards]);
}

interface DeckShelfProps {
  uid: string;
  cards: any[];
  onTogglePublic?: (deckId: string, currentPublic: boolean) => void;
  onSetCoverCard?: (deckId: string) => void;
}

// Ensure each deck object includes: id, name, public, coverCardId, commanderId, colorIdentity, etc.

export default function DeckShelf(props: DeckShelfProps) {
  const { uid, cards, onTogglePublic, onSetCoverCard } = props;
  const mountedRef = useMountedRef();
  const [decks, setDecks] = useState<DeckDoc[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  useEffect(() => {
    if (!uid) { setDecks([]); setErr(null); return; }
    const col = fsCollection(db, "users", uid, "decks");
    const qy = query(col, orderBy("position", "asc"), orderBy("__name__", "asc"));
    return onSnapshot(
      qy,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as DeckDoc[];
        if (mountedRef.current) setDecks(list);
      },
      (e) => setErr(e?.message || "Failed to load decks"),
    );
  }, [uid, mountedRef]);

  const onCreate = useCallback(
    async (payload: { name: string; format: string; commanderCardId?: string | null }) => {
      if (!uid) return;

      // Derive cover + color identity from selected commander
      let cover: string | null = null;
      let ci: string[] | null = null;
      if (payload.commanderCardId) {
        const cmd = cards.find((c) => c.id === payload.commanderCardId);
        cover = (cmd?.custom_image_url || cmd?.image || null) ?? null;
        ci = Array.isArray(cmd?.color_identity) ? cmd!.color_identity! : null;
      }

      // Create deck
      const nextPos =
        decks.length > 0
          ? Math.max(...decks.map((d: any) => (typeof d.position === "number" ? d.position : 0))) + 1
          : 0;

      const deckRef = await addDoc(fsCollection(db, "users", uid, "decks"), {
        name: payload.name,
        format: payload.format,
        commander_card_id: payload.commanderCardId ?? null,
        cover_image: cover,
        color_identity: ci ?? [],
        position: nextPos,
        public: false, // <--- ensure new decks start private
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // If commander selected, add it as the first slot
      if (payload.commanderCardId) {
        await addDoc(fsCollection(db, "users", uid, "decks", deckRef.id, "slots"), {
          userCardId: payload.commanderCardId,
          role: "commander",
          createdAt: serverTimestamp(),
        });
      }

      setCreateOpen(false);
    },
    [uid, decks, cards],
  );

  const handleReorder = useCallback(
    async (dragId: string, overId: string) => {
      const from = decks.findIndex((d) => d.id === dragId);
      const to = decks.findIndex((d) => d.id === overId);
      if (from < 0 || to < 0 || from === to) return;

      const next = [...decks];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      setDecks(next);

      const batch = writeBatch(db);
      next.forEach((d, i) => {
        batch.update(doc(db, "users", uid, "decks", d.id), { position: i, updatedAt: serverTimestamp() });
      });
      await batch.commit();
    },
    [decks, uid],
  );

  return (
    <section className="space-y-3">
      <FoilStyles />
      <IllegalStyles />
      {err ? (
        <div className="rounded border border-amber-700 bg-amber-900/20 px-3 py-2 text-sm text-amber-300">{err}</div>
      ) : null}

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white">Decks</h2>
        <Button variant="outline" onClick={() => setCreateOpen(true)}>Create deck</Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
        {decks.map((deck) => (
          <DeckCover
            key={deck.id}
            deck={deck}
            onReorder={handleReorder}
            uid={uid}
            cards={cards}
            readOnly={false}
            onOpenDeck={() => {}}
            onTogglePublic={onTogglePublic}
            onSetCoverCard={onSetCoverCard}
          />
        ))}
      </div>

      <style>{`
        .deck-tiles .illegal-popover, .deck-tiles .oracle-popover {
          font-size: 10px; line-height: 1.15;
        }
      `}</style>

      <CreateDeckModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        cards={cards}
        onCreate={onCreate}
      />
    </section>
  );
}

function DeckCover({
  deck,
  onReorder,
  uid,
  cards,
  readOnly = false,
  onOpenDeck,
  onTogglePublic,
  onSetCoverCard,
}: {
  deck: DeckDoc;
  onReorder: (dragId: string, overId: string) => void;
  uid: string | null;
  cards: UserCardLite[];
  readOnly?: boolean;
  onOpenDeck: () => void;
  onTogglePublic?: (deckId: string, currentPublic: boolean) => void;
  onSetCoverCard?: (deckId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isPublic = !!deck.public; // <--- define

  const handleDragStart = (e: React.DragEvent) => {
    if (readOnly) return;
    e.dataTransfer.setData("text/deck-id", deck.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleCardDropOnCover = async (e: React.DragEvent) => {
    if (readOnly || !uid) return;
    e.preventDefault();
    e.stopPropagation();
    const data = getDropPayload(e);
    const userCardId = data?.userCardId;
    if (!userCardId) return;
    await addDoc(fsCollection(db, "users", uid, "decks", deck.id, "slots"), {
      userCardId,
      createdAt: serverTimestamp(),
    });
  };

  const ci = deck.color_identity || [];
  const commanderName = useMemo(() => {
    if (!deck.commander_card_id) return null;
    const c = cards.find((x) => x.id === deck.commander_card_id);
    return c?.name ?? null;
  }, [deck.commander_card_id, cards]);

  const cardCount = useDeckCardCount(uid, deck.id);

  return (
    <article
      className="relative rounded border border-gray-700 bg-black/40 overflow-hidden"
      draggable={!readOnly}
      onDragStart={handleDragStart}
      onDragOver={(e) => { if (!readOnly) allowDrop(e); }}
      onDrop={handleCardDropOnCover}
      title={readOnly ? undefined : "Drop a card to add to this deck"}
    >
      <div
        className="relative aspect-[3/4] bg-black cursor-pointer"
        role="button"
        title="Open deck"
        onClick={() => setOpen(true)}
      >
        <img
          src={deck.cover_image || "/images/cardback.png"}
          alt={deck.name}
          className="h-full w-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/10 text-white p-2 flex flex-col justify-end">
          {!readOnly && (
            <>
              {/* Delete */}
              <button
                type="button"
                className="absolute top-1 right-1 p-1.5 rounded-md bg-black/60 hover:bg-black/70 text-red-400 shadow border border-white/10"
                title="Delete deck"
                onClick={async (e) => {
                  e.stopPropagation();
                  if (!uid) return;
                  if (confirm("Delete deck? Cards remain in your collection.")) {
                    await deleteDeck(uid, deck.id);
                  }
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <path d="M6 6l12 12M6 18L18 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              {/* Visibility toggle */}
              <button
                type="button"
                className={`absolute top-9 right-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${
                  isPublic
                    ? "bg-emerald-600/80 hover:bg-emerald-600 text-white"
                    : "bg-slate-600/70 hover:bg-slate-600 text-white/90"
                }`}
                title={isPublic ? "Set Private" : "Set Public"}
                onClick={(e) => {
                  e.stopPropagation();
                  onTogglePublic?.(deck.id, isPublic);
                }}
              >
                {isPublic ? "Public" : "Private"}
              </button>
              {/* Set cover button */}
              <button
                type="button"
                className="absolute top-16 right-1 px-1.5 py-0.5 rounded text-[10px] bg-indigo-600/70 hover:bg-indigo-600 text-white"
                title="Set cover card (use selected / commander)"
                onClick={(e) => {
                  e.stopPropagation();
                  onSetCoverCard?.(deck.id);
                }}
              >
                Cover
              </button>
            </>
          )}
          <div className="text-sm font-semibold truncate pr-12">{deck.name}</div>
          <div className="text-xs opacity-90">{deck.format}</div>
          <div className="mt-1 text-[11px] opacity-90 flex items-center gap-2">
            {commanderName ? (
              <>
                <span className="truncate">Commander: {commanderName}</span>
                <ColorPips ci={ci || []} />
              </>
            ) : null}
          </div>
          <div className="mt-1 text-[11px] opacity-90">Cards: {cardCount}</div>
        </div>
      </div>

      <DeckViewer
        uid={uid}
        deck={deck}
        open={open}
        onOpenChange={setOpen}
        cards={cards}
        readOnly={readOnly}
      />
    </article>
  );
}

function useDeckCardCount(uid: string | null, deckId: string) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!uid || !deckId) { setCount(0); return; }
    const col = fsCollection(db, "users", uid, "decks", deckId, "slots");
    return onSnapshot(col, (snap) => setCount(snap.size));
  }, [uid, deckId]);
  return count;
}

function getTypeGroup(tl?: string | null) {
  const s = (tl || "").toLowerCase();
  if (!s) return "Unknown";
  if (s.includes("creature")) return "Creature";
  if (s.includes("instant")) return "Instant";
  if (s.includes("sorcery")) return "Sorcery";
  if (s.includes("enchantment")) return "Enchantment";
  if (s.includes("artifact")) return "Artifact";
  if (s.includes("planeswalker")) return "Planeswalker";
  if (s.includes("battle")) return "Battle";
  if (s.includes("land")) return "Land";
  return "Other";
}
function getColorGroup(ci?: string[] | null, colors?: string[] | null) {
  const src = (Array.isArray(ci) && ci.length ? ci : Array.isArray(colors) ? colors : []) as string[];
  const set = new Set(src.map((x) => String(x).toUpperCase()));
  const order = ["W", "U", "B", "R", "G"];
  const present = order.filter((x) => set.has(x));
  if (present.length === 0) return "Colorless";
  if (present.length === 1) {
    const map: Record<string, string> = { W: "White", U: "Blue", B: "Black", R: "Red", G: "Green" };
    return map[present[0]] || "Colorless";
  }
  return "Multicolor";
}

function rarityRank(r?: string | null) {
  const key = String(r || "").toLowerCase();
  const order: Record<string, number> = {
    common: 0,
    c: 0,
    uncommon: 1,
    u: 1,
    rare: 2,
    r: 2,
    mythic: 3,
    m: 3,
    special: 4,
    bonus: 4,
    timeshifted: 4,
    masterpiece: 5,
    mythic_rare: 3,
  };
  return key in order ? order[key] : 99;
}
function getCardPriceUSD(c: UserCardLite | null | undefined) {
  const p = (c as any)?.scryfall_full?.prices;
  const v = p?.usd ?? p?.usd_foil ?? p?.usd_etched ?? null;
  const n = typeof v === "string" ? parseFloat(v) : typeof v === "number" ? v : NaN;
  return Number.isFinite(n) ? n : Number.NaN;
}

function getListingMeta(c: UserCardLite | null | undefined) {
  const rarity = (c as any)?.rarity ?? null;
  const setCode = (c as any)?.set_code ?? null;
  const setName = (c as any)?.set_name ?? null;
  const cn = (c as any)?.collector_number ?? (c as any)?.cn ?? null;
  return { rarity, setCode, setName, cn };
}
function getListingPrices(c: UserCardLite | null | undefined) {
  const price = (c as any)?.price;
  const foil = (c as any)?.foil_price ?? (c as any)?.price_foil;
  const fmt = (v: any) => (typeof v === "number" && Number.isFinite(v) ? `$${v.toFixed(2)}` : "—");
  return { priceText: fmt(price), foilText: fmt(foil) };
}

function DeckViewer({
  uid,
  deck,
  open,
  onOpenChange,
  cards,
  readOnly = false,
}: {
  uid: string | null;
  deck: DeckDoc;
  open: boolean;
  onOpenChange: (o: boolean) => void;
  cards: UserCardLite[];
  readOnly?: boolean;
}) {
  const [slots, setSlots] = useState<DeckSlot[]>([]);

  useEffect(() => {
    if (!open || !uid) return;
    const colRef = fsCollection(db, "users", uid, "decks", deck.id, "slots");
    const qy = query(colRef, orderBy("createdAt", "asc"), orderBy("__name__", "asc"));
    const unsub = onSnapshot(qy, (snap) => {
      const rows = snap.docs.map((d) => {
        const v = d.data() as any;
        // normalize userCardId for legacy docs
        const raw = v?.userCardId ?? v?.cardId ?? v?.user_card_id ?? null;
        return { id: d.id, userCardId: raw != null ? String(raw) : "", role: v?.role ?? null, createdAt: v?.createdAt ?? null } as DeckSlot;
      });
      setSlots(rows);
    });
    return () => unsub();
  }, [uid, deck.id, open]);

  const byId = useMemo(() => {
    const m = new Map<string, UserCardLite>();
    for (const c of cards) m.set(c.id, c);
    return m;
  }, [cards]);

  const commanderCardId = deck.commander_card_id || null;
  const commanderCI = Array.isArray(deck.color_identity) ? deck.color_identity.map((x) => String(x).toUpperCase()) : [];
  const commanderCard = useMemo(() => (commanderCardId ? byId.get(commanderCardId) || null : null), [byId, commanderCardId]);
  const commanderImg = commanderCard ? (commanderCard.custom_image_url || commanderCard.image || "/images/cardback.png") : null;

  // Flatten slots -> cards (+commander first)
  const items = useMemo(() => {
    const rows = slots.map((s: any) => {
      const card = byId.get(s.userCardId);
      return { slot: s, card: card || null };
    });
    if (!commanderCardId) return rows;
    const [cmd, rest] = rows.reduce<
      [{ slot: any; card: UserCardLite | null } | null, Array<{ slot: any; card: UserCardLite | null }>]
    >(
      (acc, x) => {
        if (!acc[0] && x.card && x.card.id === commanderCardId) {
          acc[0] = x;
        } else {
          acc[1].push(x);
        }
        return acc;
      },
      [null, []],
    );
    return cmd ? [cmd, ...rest] : rows;
  }, [slots, byId, commanderCardId]);

  // Grouping/sorting for DECK grid
  type DeckGroupBy = "none" | "type" | "color" | "mana";
  type DeckSortBy = "name" | "mana" | "rarity" | "price";
  type SortDir = "asc" | "desc";
  const [deckGroupBy, setDeckGroupBy] = useState<DeckGroupBy>("none");
  const [deckSortBy, setDeckSortBy] = useState<DeckSortBy>("name");
  const [deckSortDir, setDeckSortDir] = useState<SortDir>("asc");

  const sortDeck = useCallback(
    (arr: Array<{ slot: DeckSlot; card: UserCardLite | null }>) => {
      const copy = [...arr];
      copy.sort((a, b) => {
        const ca = a.card, cb = b.card;
        if (!ca && !cb) return 0;
        if (!ca) return 1;
        if (!cb) return -1;

        let cmp = 0;
        if (deckSortBy === "name") {
          cmp = String(ca.name || "").toLowerCase()
            .localeCompare(String(cb.name || "").toLowerCase(), undefined, { sensitivity: "base", numeric: true });
        } else if (deckSortBy === "mana") {
          const va = typeof ca.mana_value === "number" ? ca.mana_value : Number.POSITIVE_INFINITY;
          const vb = typeof cb.mana_value === "number" ? cb.mana_value : Number.POSITIVE_INFINITY;
          cmp = va - vb;
        } else if (deckSortBy === "rarity") {
          cmp = rarityRank(ca?.scryfall_full?.rarity) - rarityRank(cb?.scryfall_full?.rarity);
        } else if (deckSortBy === "price") {
          const pa = getCardPriceUSD(ca);
          const pb = getCardPriceUSD(cb);
          // Missing price sorts last in asc, first in desc (treat NaN as -Infinity for desc, +Infinity for asc)
          const va = Number.isFinite(pa) ? pa : (deckSortDir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
          const vb = Number.isFinite(pb) ? pb : (deckSortDir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
          cmp = va - vb;
        }

        return deckSortDir === "asc" ? cmp : -cmp;
      });
      return copy;
    },
    [deckSortBy, deckSortDir],
  );

  const groupedDeck = useMemo(() => {
    if (deckGroupBy === "none")
      return null as null | Map<string, Array<{ slot: DeckSlot; card: UserCardLite | null }>>;
    const map = new Map<string, Array<{ slot: DeckSlot; card: UserCardLite | null }>>();
    for (const row of items) {
      const c = row.card;
      let key = "Other";
      switch (deckGroupBy) {
        case "type":
          key = getTypeGroup(c?.type_line ?? null);
          break;
        case "color":
          key = getColorGroup(c?.color_identity ?? null, c?.colors ?? null);
          break;
        case "mana":
          key = typeof c?.mana_value === "number" ? `MV ${c!.mana_value}` : "MV —";
          break;
      }
      const bucket = map.get(key);
      if (bucket) bucket.push(row);
      else map.set(key, [row]);
    }
    for (const [k, v] of map.entries()) {
      const sorted = sortDeck(v);
      if (deckGroupBy === "type" && commanderCardId) {
        const idx = sorted.findIndex((r) => r.card?.id === commanderCardId);
        if (idx > 0) {
          const [cmd] = sorted.splice(idx, 1);
          sorted.unshift(cmd);
        }
      }
      map.set(k, sorted);
    }
    return map;
  }, [items, deckGroupBy, sortDeck, commanderCardId]);

  const usedIds = useMemo(() => new Set(slots.map((s: any) => s.userCardId)), [slots]);

  const onDropAdd = async (e: React.DragEvent) => {
    if (readOnly || !uid) return;
    e.preventDefault();
    const data = getDropPayload(e);
    const userCardId = data?.userCardId;
    if (!userCardId) return;
    await addDoc(fsCollection(db, "users", uid, "decks", deck.id, "slots"), {
      userCardId,
      createdAt: serverTimestamp(),
    });
  };

  const illegalReason = useCallback((c: UserCardLite | null) => {
    if (!c) return null;
    const res = isIllegalInFormat(c.scryfall_full || null, deck.format || "");
    return res?.illegal ? (res.reason || "Illegal") : null;
  }, [deck.format]);

  const isOffColor = useCallback((c: UserCardLite | null) => {
    if (!c) return false;
    if (!commanderCardId || !commanderCI.length) return false;
    const ci = Array.isArray(c.color_identity) ? c.color_identity.map((x) => String(x).toUpperCase()) : [];
    if (ci.length === 0) return false;
    return ci.some((col) => !commanderCI.includes(col));
  }, [commanderCardId, commanderCI]);

  // Export decklist
  const buildExport = useCallback(() => {
    const counts = new Map<string, number>();
    let commanderName: string | null = null;
    for (const { card } of items) {
      if (!card) continue;
      if (commanderCardId && card.id === commanderCardId) {
        commanderName = card.name;
        continue;
      }
      counts.set(card.name, (counts.get(card.name) || 0) + 1);
    }
    const lines: string[] = [];
    lines.push(`# ${deck.name}`);
    if (deck.format) lines.push(`# Format: ${deck.format}`);
    if (Array.isArray(deck.color_identity) && deck.color_identity.length) {
      lines.push(`# Colors: ${deck.color_identity.join("")}`);
    }
    if (commanderName) {
      lines.push("");
      lines.push("Commander");
      lines.push(`1 ${commanderName}`);
    }
    if (counts.size) {
      lines.push("");
      lines.push("Deck");
      const sorted = [...counts.entries()].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));
      for (const [name, n] of sorted) lines.push(`${n} ${name}`);
    }
    return lines.join("\n");
  }, [items, deck.name, deck.format, deck.color_identity, commanderCardId]);

  const copyExport = async () => {
    try {
      const text = buildExport();
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(text);
      else {
        const el = document.createElement("textarea");
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand("copy");
        document.body.removeChild(el);
      }
      alert("Decklist copied to clipboard.");
    } catch {
      alert("Failed to copy decklist.");
    }
  };

  // Add-from-collection controls
  type AddGroupBy = "none" | "type" | "color";
  type AddSortBy = "name" | "mana" | "rarity" | "price";
  // avoid redeclaring SortDir used above
  type AddSortDir = "asc" | "desc";
  const [addOpen, setAddOpen] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addGroupBy, setAddGroupBy] = useState<AddGroupBy>("none");
  const [addSortBy, setAddSortBy] = useState<AddSortBy>("name");
  const [addSortDir, setAddSortDir] = useState<AddSortDir>("asc");

  const filteredForAdd = useMemo(() => {
    const base = cards.filter((c) => !usedIds.has(c.id));
    const q = addSearch.trim().toLowerCase();
    const searched = q ? base.filter((c) => c.name.toLowerCase().includes(q)) : base;
    const sorted = [...searched].sort((a, b) => {
      if (addSortBy === "name") {
        const cmp = a.name.toLowerCase().localeCompare(b.name.toLowerCase(), undefined, { numeric: true });
        return addSortDir === "asc" ? cmp : -cmp;
      } else if (addSortBy === "mana") {
        const va = typeof a.mana_value === "number" ? a.mana_value : Number.POSITIVE_INFINITY;
        const vb = typeof b.mana_value === "number" ? b.mana_value : Number.POSITIVE_INFINITY;
        const cmp = va - vb;
        return addSortDir === "asc" ? cmp : -cmp;
      } else if (addSortBy === "rarity") {
        const cmp = rarityRank(a?.scryfall_full?.rarity) - rarityRank(b?.scryfall_full?.rarity);
        return addSortDir === "asc" ? cmp : -cmp;
      } else {
        // price
        const pa = getCardPriceUSD(a);
        const pb = getCardPriceUSD(b);
        const va = Number.isFinite(pa) ? pa : (addSortDir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
        const vb = Number.isFinite(pb) ? pb : (addSortDir === "asc" ? Number.POSITIVE_INFINITY : Number.NEGATIVE_INFINITY);
        const cmp = va - vb;
        return addSortDir === "asc" ? cmp : -cmp;
      }
    });
    // group preserved
    if (addGroupBy === "none") {
      return { grouped: null as null | Map<string, UserCardLite[]>, flat: sorted };
    }
    const map = new Map<string, UserCardLite[]>();
    for (const c of sorted) {
      const key =
        addGroupBy === "type"
          ? getTypeGroup(c.type_line ?? null)
          : getColorGroup(c.color_identity ?? null, c.colors ?? null);
      const bucket = map.get(key);
      if (bucket) bucket.push(c);
      else map.set(key, [c]);
    }
    return { grouped: map, flat: [] as UserCardLite[] };
  }, [cards, usedIds, addSearch, addGroupBy, addSortBy, addSortDir]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-4xl max-h-[min(100vh-3rem,85vh)] overflow-y-auto p-0">
        <div className="px-4 pt-4">
          <div className="flex items-start gap-3">
            {commanderImg ? (
              <div
                className="relative aspect-[3/4] w-24 sm:w-28 rounded border border-white/10 overflow-hidden shadow shrink-0"
                title={commanderCard?.name || "Commander"}
              >
                <img
                  src={commanderImg}
                  alt={commanderCard?.name || "Commander"}
                  className="h-full w-full object-cover"
                />
                <div className="absolute bottom-1 left-1 rounded bg-yellow-500/90 text-black text-[11px] px-1.5 py-0.5 font-semibold">
                  Commander
                </div>
              </div>
            ) : null}

            <div className="min-w-0 flex-1">
              <DialogHeader className="px-0 pt-0">
                <DialogTitle className="truncate">{deck.name}</DialogTitle>
                <DialogDescription>
                  <span className="inline-flex items-center gap-2">
                    <span>{deck.format || "Deck"}</span>
                    <span>•</span>
                    <span>{slots.length} cards</span>
                    {Array.isArray(deck.color_identity) && deck.color_identity.length ? (
                      <>
                        <span>•</span>
                        <ColorPips ci={deck.color_identity} />
                      </>
                    ) : null}
                  </span>
                </DialogDescription>
              </DialogHeader>

              {/* Toolbar: Export + Deck group/sort */}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button variant="outline" onClick={copyExport} title="Copy decklist">Export decklist</Button>
                <div className="ml-auto flex flex-wrap items-center gap-2">
                  <label className="text-sm text-slate-200">Deck group</label>
                  <select
                    className="rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-gray-100"
                    value={deckGroupBy}
                    onChange={(e) => setDeckGroupBy(e.target.value as any)}
                  >
                    <option value="none">None</option>
                    <option value="type">Type</option>
                    <option value="color">Color</option>
                    <option value="mana">Mana</option>
                  </select>
                  <label className="text-sm text-slate-200">Sort</label>
                  <select
                    className="rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-gray-100"
                    value={deckSortBy}
                    onChange={(e) => setDeckSortBy(e.target.value as any)}
                  >
                    <option value="name">Name</option>
                    <option value="mana">Mana</option>
                    <option value="rarity">Rarity</option>
                    <option value="price">Price</option>
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-white/10 bg-black/40 text-gray-100 text-sm"
                    onClick={() => setDeckSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    title={`Direction: ${deckSortDir}`}
                  >
                    {deckSortDir === "asc" ? "Asc" : "Desc"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Drop area for adding */}
        <div
          className="mx-4 mb-4 min-h-[300px] border border-white/10 rounded p-2 bg-black/40"
          onDragOver={readOnly ? undefined : allowDrop}
          onDrop={readOnly ? undefined : onDropAdd}
          title={readOnly ? undefined : "Drag cards here to add to the deck"}
        >
          {deckGroupBy === "none" ? (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 deck-tiles">
              {sortDeck(items).map(({ slot, card }) => {
                if (!card) return null;
                const isCommander = deck.commander_card_id === card.id;
                const illegalFmt = illegalReason(card);
                const offColor = isOffColor(card);
                const isBad = !!illegalFmt || offColor;
                const img = card.custom_image_url || card.image || "/images/cardback.png";

                return (
                  <article
                    key={slot.id}
                    className={`group relative rounded border overflow-hidden ${
                      isCommander
                        ? "border-yellow-400 shadow-[0_0_0_3px_rgba(250,204,21,.8)]"
                        : isBad
                        ? "border-red-500 illegal-glow"
                        : "border-gray-700"
                    } bg-black/30`}
                    title={illegalFmt || (offColor ? "Card color identity not in commander's colors" : undefined) || undefined}
                  >
                    <div className={`relative aspect-[3/4] ${isBad ? "illegal-wiggle" : ""}`}>
                      <img src={img} alt={card.name} className="h-full w-full object-cover" />

                      {/* Hover legality/details overlay */}
                      {(() => {
                        const oracle = (card.scryfall_full?.oracle_text as string | undefined) || "";
                        const legalities = card.scryfall_full?.legalities || {};
                        const deckLegality = isBad ? `Not legal${illegalFmt ? ` — ${illegalFmt}` : offColor ? " — Off-color" : ""}` : "Legal";
                        const meta = getListingMeta(card);
                        const prices = getListingPrices(card);
                        return (
                          <div className="pointer-events-none absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                            <div className="absolute inset-0 p-1.5 flex flex-col gap-1">
                              <div className="flex-1 overflow-auto text-[10px] leading-snug text-slate-100 whitespace-pre-wrap">
                                {oracle || "No oracle text available."}
                              </div>
                              <div className="space-y-0.5 text-[10px] leading-tight text-slate-200">
                                <div className={`font-semibold ${isBad ? "text-red-200" : "text-green-200"}`}>
                                  Deck Legality: {deckLegality}
                                </div>
                                <div className="max-h-14 overflow-auto pr-1">
                                  {Object.keys(legalities).length ? (
                                    <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                      {Object.entries(legalities).map(([fmt, status]) => (
                                        <div key={fmt} className="flex justify-between gap-2">
                                          <span className="uppercase text-[9px] text-slate-300">{fmt}</span>
                                          <span className="text-[9px]">{String(status).replace(/_/g, " ")}</span>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-[9px] text-slate-400">No format data.</div>
                                  )}
                                </div>
                                <div className="flex flex-wrap gap-x-2">
                                  <span>Rarity: {meta.rarity ?? "—"}</span>
                                  <span>Set: {meta.set_name ?? meta.setCode ?? "—"}</span>
                                  <span>No: {meta.cn ?? "—"}</span>
                                </div>
                                <div className="flex flex-wrap gap-x-2">
                                  <span>Price: {prices.priceText}</span>
                                  <span>Foil: {prices.foilText}</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                      {!readOnly && (
                        <CardActions
                          uid={uid}
                          deckId={deck.id}
                          slotId={slot.id}
                          card={card}
                        />
                      )}
                      {isCommander ? (
                        <div className="absolute bottom-1 left-1 rounded bg-yellow-500/90 text-black text-[11px] px-1.5 py-0.5 font-semibold">Commander</div>
                      ) : null}
                    </div>
                    <div className="px-2 py-1 text-xs text-slate-200 truncate">{card.name}</div>
                  </article>
                );
              })}
            </div>
          ) : (
            <>
              {Array.from(groupedDeck!.keys()).sort((a, b) => a.localeCompare(b)).map((key) => {
                const rows = groupedDeck!.get(key)!;
                return (
                  <div key={key} className="mb-3">
                    <div className="text-sm font-semibold text-white mb-2">{key}</div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-3 deck-tiles">
                      {rows.map(({ slot, card }) => {
                        if (!card) return null;
                        const isCommander = deck.commander_card_id === card.id;
                        const illegalFmt = illegalReason(card);
                        const offColor = isOffColor(card);
                        const isBad = !!illegalFmt || offColor;
                        const img = card.custom_image_url || card.image || "/images/cardback.png";

                        return (
                          <article
                            key={slot.id}
                            className={`group relative rounded border overflow-hidden ${
                              isCommander
                                ? "border-yellow-400 shadow-[0_0_0_3px_rgba(250,204,21,.8)]"
                                : isBad
                                ? "border-red-500 illegal-glow"
                                : "border-gray-700"
                            } bg-black/30`}
                            title={illegalFmt || (offColor ? "Card color identity not in commander's colors" : undefined) || undefined}
                          >
                            <div className={`relative aspect-[3/4] ${isBad ? "illegal-wiggle" : ""}`}>
                              <img src={img} alt={card.name} className="h-full w-full object-cover" />

                              {/* Hover legality/details overlay */}
                              {(() => {
                                const oracle = (card.scryfall_full?.oracle_text as string | undefined) || "";
                                const legalities = card.scryfall_full?.legalities || {};
                                const deckLegality = isBad ? `Not legal${illegalFmt ? ` — ${illegalFmt}` : offColor ? " — Off-color" : ""}` : "Legal";
                                const meta = getListingMeta(card);
                                const prices = getListingPrices(card);
                                return (
                                  <div className="pointer-events-none absolute inset-0 bg-black/70 opacity-0 group-hover:opacity-100 transition-opacity">
                                    <div className="absolute inset-0 p-1.5 flex flex-col gap-1">
                                      <div className="flex-1 overflow-auto text-[10px] leading-snug text-slate-100 whitespace-pre-wrap">
                                        {oracle || "No oracle text available."}
                                      </div>
                                      <div className="space-y-0.5 text-[10px] leading-tight text-slate-200">
                                        <div className={`font-semibold ${isBad ? "text-red-200" : "text-green-200"}`}>
                                          Deck Legality: {deckLegality}
                                        </div>
                                        <div className="max-h-14 overflow-auto pr-1">
                                          {Object.keys(legalities).length ? (
                                            <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                                              {Object.entries(legalities).map(([fmt, status]) => (
                                                <div key={fmt} className="flex justify-between gap-2">
                                                  <span className="uppercase text-[9px] text-slate-300">{fmt}</span>
                                                  <span className="text-[9px]">{String(status).replace(/_/g, " ")}</span>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <div className="text-[9px] text-slate-400">No format data.</div>
                                          )}
                                        </div>
                                        <div className="flex flex-wrap gap-x-2">
                                          <span>Rarity: {meta.rarity ?? "—"}</span>
                                          <span>Set: {meta.set_name ?? meta.setCode ?? "—"}</span>
                                          <span>No: {meta.cn ?? "—"}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-x-2">
                                          <span>Price: {prices.priceText}</span>
                                          <span>Foil: {prices.foilText}</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}

                              {!readOnly && (
                                <CardActions
                                  uid={uid}
                                  deckId={deck.id}
                                  slotId={slot.id}
                                  card={card}
                                />
                              )}
                              {isCommander ? (
                                <div className="absolute bottom-1 left-1 rounded bg-yellow-500/90 text-black text-[11px] px-1.5 py-0.5 font-semibold">Commander</div>
                              ) : null}
                            </div>
                            <div className="px-2 py-1 text-xs text-slate-200 truncate">{card.name}</div>
                          </article>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Collapsible Add-from-collection panel */}
        {!readOnly && (
          <div className="px-4 pb-4">
            <button
              type="button"
              onClick={() => setAddOpen((o) => !o)}
              className="w-full flex items-center justify-between rounded border border-white/10 bg-black/50 hover:bg-black/60 px-3 py-2 text-left text-slate-200"
              title="Add cards from your collection"
            >
              <span className="font-medium">Collection</span>
              <span className="text-xs opacity-80">{addOpen ? "Hide" : "Show"}</span>
            </button>

            {addOpen && (
              <div className="mt-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    value={addSearch}
                    onChange={(e) => setAddSearch(e.target.value)}
                    placeholder="Search your collection…"
                    className="rounded border border-gray-700 bg-black/40 px-2 py-1.5 text-sm text-gray-100"
                  />
                  <label className="text-sm text-slate-200">Group</label>
                  <select
                    className="rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-gray-100"
                    value={addGroupBy}
                    onChange={(e) => setAddGroupBy(e.target.value as any)}
                  >
                    <option value="none">None</option>
                    <option value="type">Type</option>
                    <option value="color">Color</option>
                  </select>
                  <label className="text-sm text-slate-200">Sort</label>
                  <select
                    className="rounded border border-white/10 bg-black/40 px-2 py-1 text-sm text-gray-100"
                    value={addSortBy}
                    onChange={(e) => setAddSortBy(e.target.value as any)}
                  >
                    <option value="name">Name</option>
                    <option value="mana">Mana</option>
                    <option value="rarity">Rarity</option>
                    <option value="price">Price</option>
                  </select>
                  <button
                    type="button"
                    className="px-2 py-1 rounded border border-white/10 bg-black/40 text-gray-100 text-sm"
                    onClick={() => setAddSortDir((d) => (d === "asc" ? "desc" : "asc"))}
                    title={`Direction: ${addSortDir}`}
                  >
                    {addSortDir === "asc" ? "Asc" : "Desc"}
                  </button>
                </div>

                {filteredForAdd.grouped ? (
                  Array.from(filteredForAdd.grouped.keys()).sort((a, b) => a.localeCompare(b)).map((key) => {
                    const rows = filteredForAdd.grouped!.get(key)!;
                    return (
                      <div key={key} className="mb-2">
                        <div className="text-sm font-semibold text-white mb-1">{key}</div>
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                          {rows.slice(0, 60).map((c) => {
                            const img = c.custom_image_url || c.image || "/images/cardback.png";
                            return (
                              <button
                                key={c.id}
                                className="relative aspect-[3/4] rounded border border-gray-700 overflow-hidden hover:border-slate-400"
                                onClick={async () => {
                                  if (!uid) return;
                                  await addDoc(fsCollection(db, "users", uid, "decks", deck.id, "slots"), {
                                    userCardId: c.id,
                                    createdAt: serverTimestamp(),
                                  });
                                }}
                                title={`Add ${c.name}`}
                              >
                                <img src={img} alt={c.name} className="w-full h-full object-cover" />
                                <div className="absolute bottom-0 left-0 right-0 text-[10px] text-white/90 bg-black/50 px-1 py-0.5 truncate">
                                  {c.name}
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                    {filteredForAdd.flat.slice(0, 60).map((c) => {
                      const img = c.custom_image_url || c.image || "/images/cardback.png";
                      return (
                        <button
                          key={c.id}
                          className="relative aspect-[3/4] rounded border border-gray-700 overflow-hidden hover:border-slate-400"
                          onClick={async () => {
                            if (!uid) return;
                            await addDoc(fsCollection(db, "users", uid, "decks", deck.id, "slots"), {
                              userCardId: c.id,
                              createdAt: serverTimestamp(),
                            });
                          }}
                          title={`Add ${c.name}`}
                        >
                          <img src={img} alt={c.name} className="w-full h-full object-cover" />
                          <div className="absolute bottom-0 left-0 right-0 text-[10px] text-white/90 bg-black/50 px-1 py-0.5 truncate">
                            {c.name}
                          </div>
                        </button>
                      );
                    })}
                    {filteredForAdd.flat.length === 0 && (
                      <div className="text-xs text-slate-400">No matching cards to add.</div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Styles for overlays + glow/jiggle */}
        <style>{`
          .deck-tiles .illegal-popover, .deck-tiles .oracle-popover {
            font-size: 10px; line-height: 1.15;
          }
          .illegal-glow {
            box-shadow: 0 0 0 3px rgba(239,68,68,.85), 0 0 14px rgba(239,68,68,.45);
          }
          @keyframes subtle-wiggle {
            0% { transform: translate3d(0,0,0) rotate(0deg); }
            25% { transform: translate3d(0.2px, 0, 0) rotate(0.2deg); }
            50% { transform: translate3d(0,0,0) rotate(0deg); }
            75% { transform: translate3d(-0.2px, 0, 0) rotate(-0.2deg); }
            100% { transform: translate3d(0,0,0) rotate(0deg); }
          }
          .illegal-wiggle { animation: subtle-wiggle 0.6s ease-in-out infinite; }
        `}</style>
      </DialogContent>
    </Dialog>
  );
}

// Small actions menu for deck cards
function CardActions({
  uid,
  deckId,
  slotId,
  card,
}: {
  uid: string | null;
  deckId: string;
  slotId: string;
  card: UserCardLite;
}) {
  const [open, setOpen] = useState(false);
  const [binders, setBinders] = useState<Array<{ id: string; name: string }>>([]);
  const [decks, setDecks] = useState<Array<{ id: string; name: string }>>([]);
  const [binderId, setBinderId] = useState<string>("");
  const [destDeckId, setDestDeckId] = useState<string>("");

  useEffect(() => {
    if (!open || !uid) return;
    // Load binders
    getDocs(fsCollection(db, "users", uid, "binders")).then((snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).map((x) => ({ id: x.id, name: x.name || "Binder" }));
      setBinders(arr);
      if (!binderId && arr.length) setBinderId(arr[0].id);
    });
    // Load decks
    getDocs(fsCollection(db, "users", uid, "decks")).then((snap) => {
      const arr = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })).map((x) => ({ id: x.id, name: x.name || "Deck" }));
      // Exclude current deck
      setDecks(arr.filter((d) => d.id !== deckId));
      if (!destDeckId && arr.length) {
        const first = arr.find((d) => d.id !== deckId);
        if (first) setDestDeckId(first.id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, uid, deckId]);

  const removeFromDeck = async () => {
    if (!uid) return;
    await deleteDoc(doc(db, "users", uid, "decks", deckId, "slots", slotId));
  };

  const deleteFromDeckAndCollection = async () => {
    if (!uid) return;
    await deleteDoc(doc(db, "users", uid, "decks", deckId, "slots", slotId));
    await deleteDoc(doc(db, "users", uid, "cards", card.id));
  };

  const moveToBinder = async () => {
    if (!uid || !binderId) return;
    await addDoc(fsCollection(db, "users", uid, "binders", binderId, "slots"), {
      userCardId: card.id,
      createdAt: serverTimestamp(),
    });
    await removeFromDeck();
  };

  const moveToDeck = async () => {
    if (!uid || !destDeckId) return;
    await addDoc(fsCollection(db, "users", uid, "decks", destDeckId, "slots"), {
      userCardId: card.id,
      createdAt: serverTimestamp(),
    });
    await removeFromDeck();
  };

  const openEdit = () => {
    // Dispatch global event so MyCollection (parent page) can open its editor modal
    window.dispatchEvent(new CustomEvent("sg:open-edit-card", { detail: { userCardId: card.id } }));
    setOpen(false);
  };

  return (
    <>
      {/* Smaller trigger button */}
      <button
        type="button"
        className="absolute top-1 right-1 z-10 p-1 rounded-md bg-black/60 hover:bg-black/70 text-white shadow border border-white/10"
        title="Actions"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        {/* 3-dot icon (small) */}
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="5" cy="12" r="2" />
          <circle cx="12" cy="12" r="2" />
          <circle cx="19" cy="12" r="2" />
        </svg>
      </button>

      {open && (
        <div
          className="absolute top-6 right-1 z-30 w-40 rounded-md border border-white/10 bg-black/85 text-slate-100 shadow-lg p-1 space-y-0.5 text-[11px] leading-tight"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            className="w-full text-left px-1.5 py-1 rounded hover:bg-white/10"
            onClick={openEdit}
            title="Edit listing details"
          >
            Edit
          </button>
          <button
            className="w-full text-left px-1.5 py-1 rounded hover:bg-white/10"
            onClick={async () => {
              if (confirm("Remove this card from this deck?")) await removeFromDeck();
              setOpen(false);
            }}
            title="Remove from this deck"
          >
            Delete from deck
          </button>
          <button
            className="w-full text-left px-1.5 py-1 rounded hover:bg-white/10 text-red-300"
            onClick={async () => {
              if (confirm("Remove from this deck AND delete it from your collection?")) {
                await deleteFromDeckAndCollection();
              }
              setOpen(false);
            }}
            title="Remove from deck and delete from collection"
          >
            Delete from deck + collection
          </button>

          <div className="h-px bg-white/10 my-1" />

          <div className="space-y-1">
            <div className="text-[10px] text-slate-300 px-1.5">Move to binder</div>
            <div className="flex items-center gap-1 px-1.5">
              <select
                value={binderId}
                onChange={(e) => setBinderId(e.target.value)}
                className="flex-1 rounded border border-white/10 bg-black/40 px-1 py-0.5 text-[11px] h-6"
              >
                {binders.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
              <button
                className="px-1.5 py-0.5 rounded border border-white/10 hover:bg-white/10 text-[11px]"
                onClick={async () => { await moveToBinder(); setOpen(false); }}
                disabled={!binderId}
                title="Move to selected binder"
              >
                Move
              </button>
            </div>
          </div>

          <div className="space-y-1">
            <div className="text-[10px] text-slate-300 px-1.5">Move to deck</div>
            <div className="flex items-center gap-1 px-1.5">
              <select
                value={destDeckId}
                onChange={(e) => setDestDeckId(e.target.value)}
                className="flex-1 rounded border border-white/10 bg-black/40 px-1 py-0.5 text-[11px] h-6"
              >
                {decks.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
              <button
                className="px-1.5 py-0.5 rounded border border-white/10 hover:bg-white/10 text-[11px]"
                onClick={async () => { await moveToDeck(); setOpen(false); }}
                disabled={!destDeckId}
                title="Move to selected deck"
              >
                Move
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function CreateDeckModal({
  open,
  onOpenChange,
  cards,
  onCreate,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  cards: UserCardLite[];
  onCreate: (p: { name: string; format: string; commanderCardId?: string | null }) => void | Promise<void>;
}) {
  const [name, setName] = useState("");
  const [format, setFormat] = useState<string>("Commander");
  const [queryStr, setQueryStr] = useState("");
  const [commanderId, setCommanderId] = useState<string | null>(null);

  const isCommander = format.toLowerCase() === "commander";
  const filtered = useMemo(() => {
    if (!isCommander) return [];
    const q = queryStr.trim().toLowerCase();
    const arr = q
      ? cards.filter((c) => `${c.name}`.toLowerCase().includes(q))
      : cards.slice(0, 30);
    return arr;
  }, [isCommander, cards, queryStr]);

  useEffect(() => {
    if (!isCommander) setCommanderId(null);
  }, [isCommander]);

  const submit = async () => {
    if (!name.trim()) return;
    await onCreate({ name: name.trim(), format, commanderCardId: isCommander ? commanderId : null });
    setName("");
    setFormat("Commander");
    setQueryStr("");
    setCommanderId(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <div className="text-white text-base font-semibold mb-2">Create deck</div>
        <div className="space-y-3">
          <label className="block text-sm text-slate-200">
            Name
            <input
              className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-2 text-gray-100"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My Deck"
            />
          </label>
          <label className="block text-sm text-slate-200">
            Format
            <select
              className="mt-1 w-full rounded border border-gray-700 bg-black/40 px-2 py-2 text-gray-100"
              value={format}
              onChange={(e) => setFormat(e.target.value)}
            >
              {PLAY_FORMATS.map((f) => (
                <option key={f} value={f}>{f}</option>
              ))}
            </select>
          </label>
          {isCommander && (
            <div className="space-y-2">
              <div className="text-sm text-slate-200">Select commander (from your collection)</div>
              <input
                className="w-full rounded border border-gray-700 bg-black/40 px-2 py-2 text-gray-100"
                value={queryStr}
                onChange={(e) => setQueryStr(e.target.value)}
                placeholder="Search your cards…"
              />
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-64 overflow-auto">
                {filtered.map((c) => {
                  const img = c.custom_image_url || c.image || "/images/cardback.png";
                  const sel = commanderId === c.id;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCommanderId(c.id)}
                      className={`relative aspect-[3/4] rounded overflow-hidden border ${sel ? "border-yellow-400 ring-2 ring-yellow-400/60" : "border-white/10"} bg-black`}
                      title={c.name}
                    >
                      <img src={img} alt={c.name} className="h-full w-full object-cover" />
                      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-[11px] text-white px-1 py-0.5 truncate">{c.name}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-white/10">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={!name.trim() || (isCommander && !commanderId)}>Create</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ===== Helpers =====
async function deleteDeck(uid: string, deckId: string) {
  const slotsCol = fsCollection(db, "users", uid, "decks", deckId, "slots");
  const snap = await getDocs(slotsCol);
  const batch = writeBatch(db);
  snap.forEach((d) => {
    batch.delete(doc(db, "users", uid, "decks", deckId, "slots", d.id));
  });
  batch.delete(doc(db, "users", uid, "decks", deckId));
  await batch.commit();

  // Cleanup public mirrors for this deck so cards get unhidden in MyCollection
  try {
    const pubSlotsCol = fsCollection(db, "users", uid, "public_deck_slots");
    const pubSnap = await getDocs(pubSlotsCol);
    const b2 = writeBatch(db);
    pubSnap.forEach((d) => {
      const v = d.data() as any;
      if (v?.deckId === deckId || d.id.startsWith(`${deckId}_`)) {
        b2.delete(d.ref);
      }
    });
    b2.delete(doc(db, "users", uid, "public_decks", deckId));
    await b2.commit();
  } catch (e) {
    console.warn("[DeckShelf] public mirror cleanup failed:", e);
  }
}

